import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';

/**
 * SENTRY, ON THE PHONE.
 *
 * This is here for one specific class of bug, and it is the class that has cost
 * this product the most: the SILENTLY DEAD LISTENER. The server emitted
 * `gameStateRestore` and the client listened for nothing. A reconnect did not
 * fail — it did nothing, quietly, forever, and no log line anywhere said so.
 *
 * A crash on somebody's phone mid-round is the same shape of problem: nobody
 * sends you their console. So we send it ourselves.
 *
 * TWO RULES, both non-negotiable:
 *
 *   1. NO DSN → NO SENTRY. Not a broken build, not a boot error, not a console
 *      warning in every dev's terminal. Just nothing. The DSN lives in
 *      VITE_SENTRY_DSN and a missing one is a fully supported state — a build
 *      must never fail because an optional observability vendor wasn't
 *      configured.
 *
 *   2. SCRUB `auth`. Firebase ID tokens ride in socket.io handshakes
 *      (`handshake.auth.token`), in Authorization headers, and in breadcrumbs
 *      that Sentry's fetch/XHR integrations collect automatically. A token in a
 *      third party's dashboard is an account takeover with extra steps. And a
 *      Firebase uid is a durable identifier for a real person — usually, here, a
 *      child — so it is hashed or it is not sent.
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/** Keys whose values never leave the device. Matched by NAME, at any depth —
 *  you cannot reliably regex a JWT, but you can refuse to send anything called
 *  `token`. Mirrors backend/src/logger.js so there is one definition of "safe". */
const SECRET_KEY_RE =
  /(token|password|secret|credential|authorization|auth|apikey|api_key|cookie|bearer|idToken)/i;

function scrub(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 5) return '[deep]';

  if (typeof value === 'string') {
    // An email under a key we didn't anticipate still must not be sent.
    return /[^@\s]+@[^@\s]+\.[^@\s]+/.test(value) ? '[email]' : value;
  }
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => scrub(v, depth + 1));

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SECRET_KEY_RE.test(k) ? '[redacted]' : scrub(v, depth + 1);
  }
  return out;
}

if (SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      release: (import.meta.env.VITE_APP_VERSION as string) || 'dev',
      // Default integrations will happily attach request headers and bodies.
      // We re-scrub everything below regardless, but say so out loud.
      sendDefaultPii: false,
      tracesSampleRate: 0.1,

      beforeSend(event) {
        try {
          if (event.request?.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.authorization;
            delete event.request.headers.Cookie;
          }
          if (event.request?.data) {
            event.request.data = scrub(event.request.data) as typeof event.request.data;
          }
          if (event.extra) {
            delete (event.extra as Record<string, unknown>).auth;
            event.extra = scrub(event.extra) as typeof event.extra;
          }
          // A uid is a person. We send that a user exists, never which one.
          if (event.user) event.user = { id: event.user.id ? 'redacted' : undefined };
          return event;
        } catch {
          // If we cannot prove an event is clean, we do not send it.
          return null;
        }
      },

      beforeBreadcrumb(crumb) {
        // The socket.io handshake is an XHR/fetch breadcrumb whose URL carries
        // `?token=…` on the polling transport. That URL is a credential.
        if (crumb.data && typeof crumb.data === 'object') {
          crumb.data = scrub(crumb.data) as typeof crumb.data;
        }
        if (typeof crumb.data?.url === 'string' && crumb.data.url.includes('token=')) {
          crumb.data.url = crumb.data.url.replace(/token=[^&]+/g, 'token=[redacted]');
        }
        return crumb;
      },
    });
  } catch {
    // An observability vendor is never allowed to stop a family from playing.
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
